import { ActionIcon, Divider, Group, Loader, Stack, Tooltip, UnstyledButton } from "@mantine/core";
import { Expand, Folder, Images, Lightbulb, Sparkle, Upload, User } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import type { Asset, AssetOrder, AssetSource } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { CreatorFilter } from "../../platform/media/CreatorFilter";
import { ProductFilter } from "../../platform/media/ProductFilter";
import { useAssetDelete } from "../../platform/media/asset-delete";
import { shapeRatio, shapeStyle } from "../../platform/media/asset-shape";
import { AssetMenu } from "../../platform/media/AssetMenu";
import { LibraryPreview } from "../../platform/media/LibraryPreview";
import { LIBRARY_ORDERS, LIBRARY_SEARCH_MAX, useLibraryPages } from "../../platform/media/library-pages";
import { useMembership } from "../../platform/membership";
import { canEditContent } from "../../platform/permissions";
import { CardPicture } from "../../ui/CardPicture";
import { EmptyState } from "../../ui/EmptyState";
import { LoadAhead } from "../../ui/LoadAhead";
import { readPref, writePref } from "../../platform/prefs";
import { ColumnsSetting } from "../../ui/ColumnsSetting";
import { MediaCard, MediaPlaceholder } from "../../ui/MediaCard";
import { usePageScroll } from "../../ui/PageColumns";
import { Soon } from "../../ui/Soon";
import { SortMenu } from "../../ui/SortMenu";
import { SubmitSearch } from "../../ui/SubmitSearch";
import { TypeTabs } from "../../ui/TypeTabs";
import { VideoFrame } from "../../ui/VideoFrame";
import { VirtualMasonry, type GridCardProps } from "../../ui/VirtualMasonry";
import type { Medium } from "../../platform/studio/draft";
import classes from "./StudioLibrary.module.css";

/** Live's page of the tab: its list names no limit, so the route's default (`limit = 20`). */
const STUDIO_PAGE = 20;

export interface StudioLibraryProps {
  teamId: string;
  brandId: string;
  medium: Medium;
}

/** The tab's search, source and order: the page's state, as live keeps them out of the URL. */
interface StudioFilter {
  search: string;
  source: AssetSource | undefined;
  createdBy: string[];
  productId: string | undefined;
  order: AssetOrder;
}

/**
 * The brand's library files of this page's medium (S1's list), under live's toolbar, in the live
 * app's masonry at the library's column count, as the Library page's: 20 a page, the next three
 * screens before the end (live's `rootMargin: '0px 0px 300% 0px'` on the page's scroll), only the
 * cards near the view mounted, each a thumbnail under live's skeleton (issue 794; it cut off at
 * its first 50). A click on a file, or its ⤢ "Open preview", opens the library's lightbox over the
 * tab's list (G1 decision 29; live's tab opens a preview on a click). The files' URLs are signed
 * for about an hour (D9) and carry no expiry, so a preview that fails to load reads the list
 * again, once.
 */
export function StudioLibrary({ teamId, brandId, medium }: StudioLibraryProps) {
  const [filter, setFilter] = useState<StudioFilter>({
    search: "",
    source: undefined,
    createdBy: [],
    productId: undefined,
    order: "newest",
  });
  const pages = useLibraryPages(
    teamId,
    brandId,
    { kind: medium, trash: false, ...filter, source: filter.source === undefined ? undefined : [filter.source] },
    true,
    { size: STUDIO_PAGE },
  );
  // The same array while the pages are: the grid places its cards again only when they change.
  const items = useMemo(() => pages.data?.pages.flatMap((page) => page.items) ?? [], [pages.data]);
  const root = usePageScroll();
  const me = useMembership(teamId);
  const mayChange = (asset: Asset) =>
    me !== undefined && canEditContent(me.role, asset.created_by ?? "", me.userId);
  const deletion = useAssetDelete(teamId, brandId);
  // The file the lightbox shows (G1 decision 29), by id; null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  // The library's column count, shared with the library page.
  const [columns, setColumns] = useState(() => readPref("libraryColumns"));
  const { refetch } = pages;
  const cardData = useMemo<CardData>(
    () => ({ teamId, brandId, open: setPreviewing, readAgain: () => void refetch() }),
    [teamId, brandId, refetch],
  );
  const more = { has: pages.hasNextPage, loading: pages.isFetchingNextPage, load: () => void pages.fetchNextPage() };
  const toolbar = (
    <LibraryToolbar
      teamId={teamId}
      medium={medium}
      filter={filter}
      onFilter={(patch) => setFilter((current) => ({ ...current, ...patch }))}
      columns={columns}
      onColumns={(next) => {
        setColumns(next);
        writePref("libraryColumns", next);
      }}
    />
  );
  let body: ReactNode;
  if (pages.isPending) body = <Loader size="sm" aria-label="Loading the library" />;
  else if (pages.isError) body = <ErrorAlert error={pages.error} />;
  else if (
    items.length === 0 &&
    (filter.search !== "" ||
      filter.source !== undefined ||
      filter.createdBy.length > 0 ||
      filter.productId !== undefined)
  ) {
    // A search or a source that finds nothing says live's words for it.
    body = (
      <EmptyState
        image={<Images size={48} strokeWidth={1.5} />}
        title="No results found"
        description="Check for typos or filters, or try a different search query."
      />
    );
  } else if (items.length === 0) {
    body = (
      <EmptyState
        image={<Images size={48} strokeWidth={1.5} />}
        title={`No ${medium === "video" ? "videos" : "images"} in the library yet`}
        description="Save a generation or upload a file, and it shows here."
      />
    );
  } else {
    body = (
      <>
        <VirtualMasonry
          items={items}
          keyOf={assetKey}
          ratioOf={shapeRatio}
          card={StudioCard}
          data={cardData}
          columns={columns}
          root={root}
          label="Library"
        />
        <LoadAhead more={more} loaded={items.length} root={root} ahead={3} label="Loading more files" />
      </>
    );
  }
  return (
    <Stack gap={12}>
      {toolbar}
      {body}
      <LibraryPreview
        teamId={teamId}
        brandId={brandId}
        assets={items}
        current={previewing}
        onShow={setPreviewing}
        more={more}
        trash={false}
        mayChange={mayChange}
        onDelete={deletion.ask}
        onRestore={() => undefined}
        restoring={null}
        paused={deletion.asking}
      />
      {deletion.dialog}
    </Stack>
  );
}

interface LibraryToolbarProps {
  teamId: string;
  medium: Medium;
  filter: StudioFilter;
  onFilter: (patch: Partial<StudioFilter>) => void;
  columns: number;
  onColumns: (columns: number) => void;
}

/**
 * The Library tab's toolbar as live has it (design-system.md 3.3, capture gaps/34): live's search
 * ("Search by asset name or partial match", on Enter or the arrow), the creator chips, the source
 * pills (Creative is the generated files, Manual Uploads the uploaded ones), a divider and the
 * Product filter (the team's products); on the right the folder scope, the sort and the display
 * settings. Inspiration, My Actors and the folder scope are Coming soon (decision 23): the list
 * route takes none of them yet.
 */
function LibraryToolbar(props: LibraryToolbarProps) {
  const { teamId, medium, filter, onFilter, columns, onColumns } = props;
  const onImage = medium === "image";
  const soon = (value: string, label: string, icon: ReactNode) => ({
    value,
    label,
    icon,
    disabled: true,
  });
  const sources = [
    { value: "all", label: "All" },
    { value: "generation", label: "Creative", icon: <Sparkle size={16} /> },
    ...(onImage ? [soon("inspiration", "Inspiration", <Lightbulb size={16} />)] : []),
    { value: "upload", label: "Manual Uploads", icon: <Upload size={16} /> },
    ...(onImage ? [soon("actors", "My Actors", <User size={16} />)] : []),
  ];
  return (
    // Live's `search-toolbar`: its row's gap `xs`, the search 335px, the pills one line (issue 828,
    // item 7: they split at 1,400px). Where the row is still too wide (the sidebar open), its
    // filters wrap below as live's groups do, never widening the pane (issue 828, item 18).
    <Group justify="space-between" gap="xs">
      <Group gap="xs">
        <SubmitSearch
          label="Search by asset name or partial match"
          value={filter.search}
          maxLength={LIBRARY_SEARCH_MAX}
          onSearch={(search) => onFilter({ search })}
          className={classes.search}
        />
        <CreatorFilter
          teamId={teamId}
          value={filter.createdBy}
          onChange={(createdBy) => onFilter({ createdBy })}
        />
        <TypeTabs
          label="Source"
          value={filter.source ?? "all"}
          onChange={(value) =>
            onFilter({ source: value === "generation" || value === "upload" ? value : undefined })
          }
          tabs={sources}
          inline
        />
        <Divider orientation="vertical" />
        <ProductFilter
          teamId={teamId}
          value={filter.productId}
          onChange={(productId) => onFilter({ productId })}
        />
      </Group>
      <Group gap={4} wrap="nowrap">
        <Soon>
          {(props) => (
            <ActionIcon {...props} variant="subtle" size="lg" aria-label="Folders">
              <Folder size={20} strokeWidth={1.5} />
            </ActionIcon>
          )}
        </Soon>
        <SortMenu
          options={LIBRARY_ORDERS}
          value={filter.order}
          onChange={(order) => onFilter({ order })}
        />
        <ColumnsSetting value={columns} onChange={onColumns} />
      </Group>
    </Group>
  );
}

/** What every card of the tab reads besides its file (`VirtualMasonry`'s `data`). */
interface CardData {
  teamId: string;
  brandId: string;
  open: (assetId: string) => void;
  readAgain: () => void;
}

const assetKey = (asset: Asset) => asset.id;

/**
 * A file of the tab: its picture, which opens the lightbox, and live's ⤢ "Open preview"; a video's
 * card also has live's Video Studio "..." (spec open-from-studio): Re-index, Open in the editor,
 * Edit and Copy Prompt.
 */
function StudioCard({ item: asset, data }: GridCardProps<Asset, CardData>) {
  const open = asset.urls.original === null ? undefined : () => data.open(asset.id);
  const corner =
    open === undefined ? undefined : (
      <Tooltip label="Open preview">
        <ActionIcon variant="text-white" className={classes.expand} aria-label={`Open preview of ${asset.name}`} onClick={open}>
          <Expand size={16} aria-hidden />
        </ActionIcon>
      </Tooltip>
    );
  return (
    <MediaCard
      still
      corner={corner}
      menu={
        asset.kind === "video" ? (
          <AssetMenu teamId={data.teamId} brandId={data.brandId} asset={asset} place="studio" />
        ) : undefined
      }
    >
      {open === undefined ? (
        <AssetPreview asset={asset} readAgain={data.readAgain} />
      ) : (
        <UnstyledButton className={classes.open} aria-label={`Preview ${asset.name}`} onClick={open}>
          <AssetPreview asset={asset} readAgain={data.readAgain} />
        </UnstyledButton>
      )}
    </MediaCard>
  );
}

/**
 * A file's picture at its shape: its thumbnail, or the image itself, or a video's poster or first
 * frame. The first load that fails reads the list again for fresh URLs; while it fails, the tab's
 * tile shows over it (a file the bucket cannot give, #959), never the browser's broken image.
 */
function AssetPreview({ asset, readAgain }: { asset: Asset; readAgain: () => void }) {
  const [retried, setRetried] = useState(false);
  const failed = () => {
    if (retried) return;
    setRetried(true);
    readAgain();
  };
  const picture = asset.urls.thumb ?? asset.urls.poster ?? (asset.kind === "image" ? asset.urls.original : null);
  const placeholder = <MediaPlaceholder label={asset.name} />;
  return (
    <div className={classes.frame} style={shapeStyle(asset)}>
      {picture !== null ? (
        <CardPicture src={picture} alt={asset.name} onFailed={failed} fallback={placeholder} />
      ) : asset.urls.original === null ? (
        placeholder
      ) : (
        <VideoFrame
          src={asset.urls.original}
          label={asset.name}
          fallback={placeholder}
          onError={failed}
        />
      )}
    </div>
  );
}
